import { AnimalPageFragment } from "@/__generated__/graphql";
import { ComponentProps } from "@components/ComponentProps";
import Hero, { CreateHeroProps } from "@components/Hero";
import IconLegend, { IconLegendItem } from "@components/IconLegend";
import { slugify } from "@/lib/utils";
import PageMeta from "@components/PageMeta";
import PreviewEditLink from "@components/PreviewEditLink";
import Slices from "@components/slices";
import SocialShareBlock from "@components/SocialShareBlock";
import { WindowWidth } from "@lib/hooks/WindowWidth";
import { useMobile } from "@lib/hooks/useMobile";
import { GlobalPageProps } from "@lib/utils";
import { GridContainer, GridWrapper } from "@theme/lib";
import { ReactElement } from "react";

export type AnimalPageProps = ComponentProps<
  AnimalPageFragment
>;

export default function AnimalPage({ id, previewEditUrl, latestVersion, versionStatus, published, path, metatags, breadcrumbs, title, content, hero, informationSection, socialLinks, pageUrl}: AnimalPageProps & GlobalPageProps): ReactElement<any> {
  const heroProps = hero ? CreateHeroProps(hero) : null;

  const is1080 = useMobile(1080);
  const is960 = useMobile(960);
  const isMobile = useMobile(WindowWidth.Mobile);

  const iconOrder = [
    'iucn-status',
    'enclosure-status',
    'area-of-zoo',
    'population-in-the-wild',
    'scientific-name',
    'order',
    'type',
    'family',
    'region',
    'habitat',
  ];

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={breadcrumbs}
        path={path}
      />
      {previewEditUrl &&
        <PreviewEditLink url={previewEditUrl} latestVersion={latestVersion} versionStatus={versionStatus} published={published} pageUrl={pageUrl}/>
      }
      {heroProps && <Hero {...heroProps} />}
      <main>
        {informationSection?.tiles?.length &&
          <GridContainer>
            <GridWrapper alignment={"full-width"}>
              <IconLegend
                toggle={isMobile}
                toggleItem="iucn-status"
                toggleLabel="Animal information"
                grid={isMobile ? 1 : is960 ? 3 : is1080 ? 4 : 5}
                items={[...informationSection.tiles]
                  .sort((a, b) => {
                    const aIndex = iconOrder.indexOf(slugify(a.label));
                    const bIndex = iconOrder.indexOf(slugify(b.label));
                    return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
                  })
                  .flatMap(({ label, value }) => {
                    const slug = slugify(label);

                    if (slug === 'enclosure-status' && !value) {
                      return [];
                    }

                    return [{
                      type: '' as IconLegendItem['type'],
                      label,
                      info: value ?? undefined,
                    }];
                  })}
              />
            </GridWrapper>
          </GridContainer>}
        {content && <Slices {...content} />}
        {(socialLinks?.linkedin || socialLinks?.facebook || socialLinks?.twitter) && (
          <GridContainer><SocialShareBlock pageUrl={pageUrl} {...socialLinks} /></GridContainer>
        )}
      </main>
    </>
  );
}
